import Phaser from "phaser";
import { Client, Room, getStateCallbacks } from "colyseus.js";
import { TilemapRenderer } from "../world/TilemapRenderer";
import { Minimap } from "../world/Minimap";
import { MAP_W_PX, MAP_H_PX, SPAWN, locateArea, TILE } from "../world/map";
import { VoiceManager } from "../voice/VoiceManager";
import { Shop } from "../world/Shop";
import { ItemRenderer, preloadItemSprites } from "../world/ItemRenderer";
import { preloadLimezuDefaults, loadManifest as loadAvatarManifest } from "../world/avatar/atlas";
import { Nameplates } from "../world/Nameplates";
import { WallScreen } from "../world/WallScreen";
import { CharacterAvatar, Appearance, DEFAULT_APPEARANCE, Effect } from "../world/CharacterAvatar";
import { CharacterCreator } from "../world/CharacterCreator";
import {
  preloadCharacters, createCharacterAnimations, makeSprite, playMove,
  MEMBER_TO_CHAR, CharKey, CHAR_KEYS,
} from "../world/Character";
import { InteractionManager, type Interactable } from "../world/InteractionManager";

/**
 * Phase 2 MainScene — KTV office with walls, collision, 6 named rooms.
 *  - MapRenderer draws the floor plan + door signs + neon
 *  - Local player is an Arcade Physics body, collides with walls
 *  - Tracks current area (lounge / kelvin / ben / ...) for Phase 4 audio scoping
 */

interface RemotePlayerVisual {
  sprite: Phaser.GameObjects.Sprite;
  label: Phaser.GameObjects.Text;
  halo: Phaser.GameObjects.Graphics;
  items: ItemRenderer;
  charKey: CharKey;
  targetX: number;
  targetY: number;
  lastX: number;
  lastY: number;
  bodyX: number;       // interpolated logical position (independent of jump offset)
  bodyY: number;
  jumpOffset: number;  // negative = sprite drawn above body for hop animation
  isJumping: boolean;
  area: string;
  direction: string;
  accentHex: string;
  mood: string;
  hatItemId: string;
  drinkItemId: string;
  carryItemId: string;
  fxItemId: string;
}

/** Map a shop fx item id to the CharacterAvatar Effect name. */
function fxItemToEffect(itemId: string): Effect {
  if (itemId === "fx_wings_angel") return "wings";
  if (itemId === "fx_aura_gold") return "aura";
  if (itemId === "fx_trail_dust") return "trail";
  return "none";
}

const MOOD_COLORS: Record<string, string> = {
  normal: "#ffffff",
  tired: "#fde047",
  frustrated: "#fb923c",
  irate: "#ef4444",
};

function hexToInt(hex: string): number {
  const h = hex.replace("#", "");
  return parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
}

const COLYSEUS_URL = (() => {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  if (location.port === "5173") {
    return `${proto}//${location.hostname}:2567`;
  }
  return `${proto}//${location.host}`;
})();

const SPEED_PX_PER_SEC = 180;
const BROADCAST_INTERVAL_MS = 50;
const INTERP_RATE = 0.18;
const LOCAL_COLOR = 0xff2e88;
const REMOTE_COLOR = 0x00f0ff;
const VOICE_RANGE_PX = 320;        // 10 tiles — beyond this, lounge audio fades out
const VOICE_FULL_RANGE_PX = 64;    // 2 tiles — within this, full volume in lounge

export class MainScene extends Phaser.Scene {
  private token: string;
  private member: { id: string; name: string; tier: string };
  private client!: Client;
  private room!: Room<any>;
  private mySessionId = "";
  private localBody!: Phaser.GameObjects.Rectangle & { body: Phaser.Physics.Arcade.Body };
  private localSprite!: Phaser.GameObjects.Sprite;
  private localCharKey!: CharKey;
  private localItems!: ItemRenderer;
  private nameplates = new Nameplates();
  private minimap = new Minimap();
  private wallScreen: WallScreen | null = null;
  private localLabel!: Phaser.GameObjects.Text;
  private localHalo!: Phaser.GameObjects.Graphics;
  private localAccent = "#ff2e88";
  private localMood = "normal";
  private dayNightOverlay!: Phaser.GameObjects.Rectangle;
  private weatherEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private remotePlayers = new Map<string, RemotePlayerVisual>();
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd!: { W: Phaser.Input.Keyboard.Key; A: Phaser.Input.Keyboard.Key; S: Phaser.Input.Keyboard.Key; D: Phaser.Input.Keyboard.Key };
  private lastBroadcast = 0;
  private direction = "down";
  private currentArea = "lounge";
  private areaHud!: Phaser.GameObjects.Text;
  private mapRenderer!: TilemapRenderer;
  private voice: VoiceManager | null = null;
  private muteBtn!: Phaser.GameObjects.Text;
  private shop: Shop | null = null;
  private lastInputAt = Date.now();
  private isIdle = false;
  private localJumpOffset = 0;
  private isLocalJumping = false;
  /** Touch joystick direction in [-1, 1]. (0,0) when joystick is idle/desktop. */
  private mobileDir = { x: 0, y: 0 };
  private interactions!: InteractionManager;
  private isInteracting = false;            // blocks WASD movement during sit
  private sitOffset = 0;                    // sprite Y offset while seated (negative = up)

  private appearance: Appearance;
  private localAvatar: CharacterAvatar | null = null;
  private remoteAvatars = new Map<string, CharacterAvatar>();

  constructor(token: string, member: { id: string; name: string; tier: string }, appearance?: Appearance) {
    super("MainScene");
    this.token = token;
    this.member = member;
    this.appearance = appearance ?? DEFAULT_APPEARANCE;
  }

  preload() {
    TilemapRenderer.preload(this);
    preloadCharacters(this);
    preloadItemSprites(this);
    preloadLimezuDefaults(this);
    // Kick off the avatar manifest fetch so ensureLayer() can resolve it
    // by the time the LimezuAvatar starts loading parts. Without this, the
    // avatar's async load chain throws "manifest not loaded" silently and
    // every layer stays as __MISSING (the "ghost player" bug).
    void loadAvatarManifest();
  }

  create() {
    this.physics.world.setBounds(0, 0, MAP_W_PX, MAP_H_PX);
    createCharacterAnimations(this);

    // Render the KTV map (floor, zones, rooms, walls)
    this.mapRenderer = new TilemapRenderer(this);
    this.mapRenderer.render();
    // Paint static minimap geometry once.
    this.minimap.paintStatic(this.mapRenderer.getMinimapConfig());

    // Invisible physics body — drives collision; the visual character rides on top.
    const local = this.add.rectangle(SPAWN.x, SPAWN.y, 16, 24, LOCAL_COLOR, 0);
    this.physics.add.existing(local);
    this.localBody = local as Phaser.GameObjects.Rectangle & { body: Phaser.Physics.Arcade.Body };
    this.localBody.body.setCollideWorldBounds(true);
    this.localBody.body.setSize(14, 14);
    this.localBody.body.setOffset(1, 8);

    this.physics.add.collider(this.localBody, this.mapRenderer.walls);

    // Visual sprite on top of physics body — hidden, replaced by CharacterAvatar
    this.localCharKey = MEMBER_TO_CHAR[this.member.id] ?? "adam";
    this.localSprite = makeSprite(this, SPAWN.x, SPAWN.y, this.localCharKey);
    this.localSprite.setDepth(50);
    this.localSprite.setVisible(false);    // procedural avatar takes over

    // Procedural customizable character (skin / hair / outfit per appearance)
    this.localAvatar = new CharacterAvatar(this, SPAWN.x, SPAWN.y);
    this.localAvatar.setAppearance(this.appearance);
    this.localAvatar.getContainer().setDepth(51);

    // Halo glow under feet (color from accent — tier-coloured per member)
    this.localHalo = this.add.graphics();
    this.localHalo.setDepth(49);
    this.drawHalo(this.localHalo, this.localAccent);

    // Equipment overlay (hat / drink / carry) — populated when shop sends loadout
    this.localItems = new ItemRenderer(this);

    // Day/night overlay (full-screen tint, alpha computed from real time)
    this.dayNightOverlay = this.add.rectangle(0, 0, MAP_W_PX, MAP_H_PX, 0x0a0828, 0)
      .setOrigin(0, 0)
      .setDepth(150)
      .setBlendMode(Phaser.BlendModes.MULTIPLY);
    this.updateDayNight();
    this.time.addEvent({ delay: 60_000, loop: true, callback: () => this.updateDayNight() });

    // Phaser text label kept for legacy "💺 sit" / "AFK" suffix swaps;
    // hidden because the glassmorphism HTML nameplate (this.nameplates)
    // renders above all character labels now.
    this.localLabel = this.add
      .text(SPAWN.x, SPAWN.y - 36, this.member.name, {
        fontFamily: "system-ui",
        fontSize: "11px",
        color: "#ffffff",
        backgroundColor: "#000000aa",
        padding: { x: 4, y: 2 },
      })
      .setOrigin(0.5, 1)
      .setDepth(100)
      .setVisible(false);

    // Camera: 1× zoom (true pixel) — character renders at 64×128 on
    // screen, viewport shows ~60×33 tiles of the 320×200-tile world.
    // Lower zoom means a wider field of view at the cost of character
    // size; raise to 1.25–1.5 if avatars feel too small.
    this.cameras.main.setBounds(0, 0, MAP_W_PX, MAP_H_PX);
    this.cameras.main.setZoom(1);
    // Use Phaser's startFollow — it computes scroll correctly given zoom.
    // (Earlier manual setScroll attempts misread the scrollX/worldView
    // relationship; Phaser internally offsets scrollX by half the
    // viewport when zoomed, so doing it ourselves over-compensates.)
    this.cameras.main.startFollow(this.localBody, true, 1, 1);
    this.cameras.main.setBackgroundColor("#1a1430");

    // Wall-mounted presence TV retired — was anchored to the old lounge stage
    // and would float over arbitrary parts of the new single-home world.
    // Re-introduce later as a proper interactable inside the home if needed.
    this.wallScreen = null;

    // Input
    this.cursors = this.input.keyboard!.createCursorKeys();
    this.wasd = {
      W: this.input.keyboard!.addKey("W"),
      A: this.input.keyboard!.addKey("A"),
      S: this.input.keyboard!.addKey("S"),
      D: this.input.keyboard!.addKey("D"),
    };

    // ───────────── Pose + effect hotkeys ─────────────
    // V = wave (auto-returns after 1.5s), B = toggle dance, N = clear pose,
    // F1 = open appearance editor (re-uses CharacterCreator). All gated when
    // chat/modal is open so typing doesn't trigger emotes.
    this.input.keyboard!.on("keydown-V", () => {
      if (this.isInteracting || this.chatInput?.style.display === "block") return;
      this.localAvatar?.setPose("wave");
    });
    this.input.keyboard!.on("keydown-B", () => {
      if (this.isInteracting || this.chatInput?.style.display === "block") return;
      const cur = this.localAvatar?.getPose();
      this.localAvatar?.setPose(cur === "dance" ? "idle" : "dance");
    });
    this.input.keyboard!.on("keydown-N", () => {
      if (this.isInteracting || this.chatInput?.style.display === "block") return;
      this.localAvatar?.setPose("idle");
    });
    this.input.keyboard!.on("keydown-F1", (e: KeyboardEvent) => {
      e.preventDefault?.();
      this.openAppearanceEditor();
    });
    // HTML "Edit look" button (#edit-look-btn) dispatches this CustomEvent.
    // We listen here so the appearance flow stays inside the scene (it has
    // the room reference + the local avatar).
    const editLookHandler = () => this.openAppearanceEditor();
    window.addEventListener("one-heart:edit-look", editLookHandler);

    // ── Mobile control listeners ──
    // Joystick emits normalized (x, y) in [-1, 1]; stored on this scene as
    // mobileDir and OR'd with keyboard input in update(). Action buttons
    // emit one-shot key intents that we map to the same handlers as the
    // keyboard hotkeys.
    const moveHandler = (e: Event) => {
      const { x, y } = (e as CustomEvent).detail;
      this.mobileDir.x = x;
      this.mobileDir.y = y;
    };
    const actionHandler = (e: Event) => {
      const { key } = (e as CustomEvent).detail;
      if (key === "e") this.interactions?.tryInteract?.();
      else if (key === "space") this.tryLocalJump();
      else if (key === "t") this.openChatInput();
      else if (key === "v") this.localAvatar?.setPose("wave");
    };
    window.addEventListener("one-heart:move", moveHandler);
    window.addEventListener("one-heart:action", actionHandler);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      window.removeEventListener("one-heart:edit-look", editLookHandler);
      window.removeEventListener("one-heart:move", moveHandler);
      window.removeEventListener("one-heart:action", actionHandler);
    });

    // Cosmetic hop on SPACE — sprite Y bounces ~16px on a sine arc, physics body
    // and broadcast position untouched. Other clients hop via "jump" message.
    this.input.keyboard!.on("keydown-SPACE", () => this.tryLocalJump());

    // B = Buy mode — opens the shop modal. Esc closes anything open.
    this.input.keyboard!.on("keydown-B", () => this.shop?.toggleModal());
    this.input.keyboard!.on("keydown-ESC", () => this.shop?.closeModal());

    // HUD pills — modern glass-morphism look, matching the HTML #hud bar.
    // Inter font, slate background, cyan accent on hover/active.
    const HUD_STYLE = {
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: "12px",
      color: "#e2e8f0",
      backgroundColor: "#0f172a99",
      padding: { x: 12, y: 6 },
      fontStyle: "500",
    } as const;

    // Coin HUD (HTML pill) sits at top-right (~y=10..46).
    // Stack the Phaser HUD pills BELOW it so they don't overlap.
    this.areaHud = this.add
      .text(this.cameras.main.width - 14, 56, "in · lounge", HUD_STYLE)
      .setScrollFactor(0)
      .setOrigin(1, 0)
      .setDepth(200);

    this.muteBtn = this.add
      .text(this.cameras.main.width - 14, 90, "🎤  connecting…", HUD_STYLE)
      .setScrollFactor(0)
      .setOrigin(1, 0)
      .setDepth(200)
      .setInteractive({ useHandCursor: true });

    this.muteBtn.on("pointerdown", () => {
      if (!this.voice || !this.voice.hasMic()) return;
      const muted = this.voice.toggleMute();
      this.muteBtn.setText(muted ? "🔇  muted · click to unmute" : "🎤  live · click to mute");
    });

    this.scale.on("resize", () => {
      this.areaHud.setX(this.cameras.main.width - 14);
      this.muteBtn.setX(this.cameras.main.width - 14);
    });

    this.setupInteractions();
    this.setupChat();
    this.connect();
  }

  /**
   * Wire up the InteractionManager — walks the hooks discovered by
   * TilemapRenderer during rendering and registers a concrete Interactable
   * for each one. Each callback owns its own state machine.
   */
  private setupInteractions() {
    this.interactions = new InteractionManager(this, this.localBody);

    for (const hook of this.mapRenderer.interactionHooks) {
      switch (hook.type) {
        case "sit": {
          const chairRef = hook.ref as { sprite: Phaser.GameObjects.Image; seatX: number; seatY: number };
          this.interactions.register({
            id: `sit-${hook.x}-${hook.y}`,
            x: hook.x, y: hook.y, radius: 38,
            label: "Sit",
            state: { sat: false },
            dynamicLabel: (self) => (self.state!.sat ? "Stand up" : "Sit"),
            available: (_, self) => !this.isInteracting || !!self.state!.sat,
            onInteract: (ctx, self) => this.actionSit(ctx, self, chairRef),
          });
          break;
        }

        case "drink-coffee":
          this.interactions.register({
            id: `coffee-${hook.x}-${hook.y}`,
            x: hook.x, y: hook.y, radius: 36,
            label: "Drink coffee",
            onInteract: (ctx) => this.actionDrink(ctx, hook.x, hook.y, "coffee"),
          });
          break;

        case "drink-alcohol":
          this.interactions.register({
            id: `bar-${hook.x}-${hook.y}`,
            x: hook.x, y: hook.y, radius: 56,
            label: "Drink at bar",
            onInteract: (ctx) => this.actionDrink(ctx, hook.x, hook.y, "alcohol"),
          });
          break;

        case "toggle-light": {
          const ref = hook.ref as {
            switchG: Phaser.GameObjects.Graphics;
            switchLabel: Phaser.GameObjects.Text;
            darkOverlay: Phaser.GameObjects.Rectangle;
          };
          this.interactions.register({
            id: `light-${hook.roomId}`,
            x: hook.x, y: hook.y, radius: 32,
            label: "Toggle light",
            state: { lightOn: true },
            dynamicLabel: (self) => (self.state!.lightOn ? "Turn lights OFF" : "Turn lights ON"),
            onInteract: (_, self) => this.actionToggleLight(ref, self),
          });
          break;
        }

        case "open-closet":
          this.interactions.register({
            id: `closet-${hook.roomId}`,
            x: hook.x, y: hook.y, radius: 40,
            label: "Open closet",
            available: () => hook.roomId === this.member.id,
            onInteract: () => this.actionOpenCloset(),
          });
          break;
      }
    }
  }

  // ──────────────────────── interaction actions ────────────────────────

  /**
   * Sit-on-chair: snap player INTO the chair seat, depth-stack the player
   * BELOW the chair sprite so the chair draws over the player's lower body
   * (the player visually "sits in" the chair, not on top of it). Force the
   * player to face down + freeze on the idle frame so they're not looping
   * a walk animation while seated.
   */
  private actionSit(
    ctx: any,
    self: Interactable,
    ref: { sprite: Phaser.GameObjects.Image; seatX: number; seatY: number },
  ) {
    if (self.state!.sat) {
      // Stand up — restore everything
      const restorePos = self.state!.standPos as { x: number; y: number };
      this.localBody.body.enable = true;
      this.localBody.setPosition(restorePos.x, restorePos.y);
      this.localSprite.setDepth(50);
      this.localAvatar?.getContainer().setDepth(51);                  // restore avatar to default depth
      this.localAvatar?.setPose("idle");                              // body un-sits via avatar pose
      this.isInteracting = false;
      this.sitOffset = 0;
      self.state!.sat = false;
      this.localLabel.setText(this.member.name);
      this.localSprite.play(`${this.localCharKey}-idle-down`);
      return;
    }

    // Remember stand-up position so we can return there
    self.state!.standPos = { x: this.localBody.x, y: this.localBody.y };

    this.localBody.body.setVelocity(0, 0);
    this.localBody.setPosition(ref.seatX, ref.sprite.y - 4);
    this.localBody.body.enable = false;
    this.localSprite.setDepth(ref.sprite.depth - 0.05);
    this.localAvatar?.getContainer().setDepth(ref.sprite.depth - 0.05); // avatar below chair too
    this.localAvatar?.setPose("sit");                                  // body curls + drops 8px
    this.direction = "down";
    this.localSprite.play(`${this.localCharKey}-idle-down`);
    this.isInteracting = true;
    this.sitOffset = 0;
    self.state!.sat = true;
    this.localLabel.setText(`${this.member.name}  •  💺`);
    ctx.floatText(ref.seatX, ref.sprite.y - 40, "💺", "#94a3b8");
  }

  private actionDrink(ctx: any, x: number, y: number, kind: "coffee" | "alcohol") {
    const tint = kind === "coffee" ? 0xfbbf24 : 0xfcd34d;
    const text = kind === "coffee" ? "+5  ☕  ENERGY" : "🥃  cheers!";
    const color = kind === "coffee" ? "#fbbf24" : "#fcd34d";
    ctx.burstParticles(x, y, tint, 16);
    ctx.floatText(x, y, text, color);
    if (kind === "alcohol") ctx.flashScreen(0xfcd34d, 220);
  }

  /**
   * Light-switch toggle — animates the per-room dark overlay rect from
   * transparent (lights on) to alpha 0.65 deep navy (lights off). Visibly
   * dramatic — players actually see the room go dark.
   */
  private actionToggleLight(
    ref: {
      switchG: Phaser.GameObjects.Graphics;
      switchLabel: Phaser.GameObjects.Text;
      darkOverlay: Phaser.GameObjects.Rectangle;
    },
    self: Interactable,
  ) {
    const wasOn = !!self.state!.lightOn;
    self.state!.lightOn = !wasOn;

    // Tween the dark overlay — 0 (lights on) ↔ 0.65 (lights off, room goes dark)
    this.tweens.add({
      targets: ref.darkOverlay,
      alpha: wasOn ? 0.65 : 0,
      duration: 380, ease: "Sine.inOut",
    });

    // Switch indicator: dim when off
    ref.switchG.setAlpha(wasOn ? 0.4 : 1.0);
    ref.switchLabel.setText(wasOn ? "💡 OFF" : "💡 ON");
    ref.switchLabel.setColor(wasOn ? "#64748b" : "#fcd34d");
  }

  /**
   * In-canvas closet modal — keeps everything inside Phaser, no HTML innerHTML
   * surface. Displays the 4 available character sprites; click one to swap.
   */
  private closetModal?: Phaser.GameObjects.Container;
  private actionOpenCloset() {
    if (this.closetModal) {
      this.closetModal.setVisible(true);
      return;
    }
    const cam = this.cameras.main;
    const w = 420;
    const h = 230;
    const cx = cam.width / 2;
    const cy = cam.height / 2;
    const container = this.add.container(cx, cy).setScrollFactor(0).setDepth(500);

    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.65);
    bg.fillRect(-cam.width / 2, -cam.height / 2, cam.width, cam.height);

    const card = this.add.graphics();
    card.fillStyle(0x0f172a, 0.95);
    card.fillRoundedRect(-w / 2, -h / 2, w, h, 14);
    card.lineStyle(1, 0x22d3ee, 0.4);
    card.strokeRoundedRect(-w / 2, -h / 2, w, h, 14);

    const title = this.add.text(0, -h / 2 + 26, "Choose your look", {
      fontFamily: "Inter, system-ui, sans-serif", fontSize: "16px", fontStyle: "700", color: "#e2e8f0",
    }).setOrigin(0.5);
    const subtitle = this.add.text(0, -h / 2 + 48, "Pick an avatar from the closet", {
      fontFamily: "Inter, system-ui, sans-serif", fontSize: "11px", color: "#94a3b8",
    }).setOrigin(0.5);

    container.add([bg, card, title, subtitle]);

    const slots = CHAR_KEYS.length;
    const slotW = 72;
    const startX = -((slots - 1) * slotW) / 2;
    for (let i = 0; i < slots; i++) {
      const ck = CHAR_KEYS[i];
      const sx = startX + i * slotW;
      const sy = 6;
      const isCurrent = ck === this.localCharKey;

      const slotBg = this.add.graphics();
      slotBg.fillStyle(isCurrent ? 0x22d3ee : 0x1e293b, isCurrent ? 0.18 : 0.5);
      slotBg.fillRoundedRect(-30, -50, 60, 96, 10);
      slotBg.lineStyle(1, isCurrent ? 0x22d3ee : 0xffffff, isCurrent ? 0.65 : 0.08);
      slotBg.strokeRoundedRect(-30, -50, 60, 96, 10);
      slotBg.x = sx; slotBg.y = sy;

      const preview = this.add.sprite(sx, sy - 8, `${ck}-idle`, 18);
      preview.setOrigin(0.5, 0.5);
      preview.setScale(0.85);

      const name = this.add.text(sx, sy + 32, ck.toUpperCase(), {
        fontFamily: "Inter, system-ui, sans-serif", fontSize: "10px", fontStyle: "600", color: "#e2e8f0",
      }).setOrigin(0.5);

      const hit = this.add.zone(sx, sy, 60, 96).setInteractive({ useHandCursor: true });
      hit.on("pointerdown", () => {
        this.localCharKey = ck;
        this.localSprite.setTexture(`${ck}-idle`, 0);
        this.localSprite.play(`${ck}-idle-down`);
        this.closetModal!.setVisible(false);
      });
      container.add([slotBg, preview, name, hit]);
    }

    // Close button
    const closeText = this.add.text(0, h / 2 - 22, "[ ESC to close ]", {
      fontFamily: "Inter, system-ui, sans-serif", fontSize: "10px", color: "#94a3b8",
    }).setOrigin(0.5);
    container.add(closeText);

    // ESC closes
    this.input.keyboard!.on("keydown-ESC", () => {
      if (this.closetModal?.visible) this.closetModal.setVisible(false);
    });
    bg.setInteractive({ useHandCursor: false });
    bg.on("pointerdown", () => this.closetModal?.setVisible(false));

    this.closetModal = container;
  }

  // ──────────────────────── appearance editor ────────────────────────

  /**
   * Re-open the CharacterCreator while logged in — drops the player into
   * the same modal new users see, but pre-loaded with their current
   * appearance. On save the live avatar updates immediately and the new
   * appearance is broadcast via the existing 'appearance-update' message.
   */
  private appearanceEditor?: CharacterCreator;
  private openAppearanceEditor() {
    if (this.appearanceEditor) return;                 // already open
    this.appearanceEditor = new CharacterCreator(this.token, this.appearance, (next) => {
      this.appearance = next;
      this.localAvatar?.setAppearance(next);
      // Tell the server so other clients see the change live
      this.room?.send("appearance-update", next);
      this.appearanceEditor = undefined;
    });
  }

  // ──────────────────────── chat system ────────────────────────

  private chatBubbles = new Map<string, { container: Phaser.GameObjects.Container; expiresAt: number }>();
  private chatInput?: HTMLInputElement;

  /**
   * Press T (or /) to open the chat input. Type a message + Enter to send.
   * The message is broadcast via Colyseus and rendered as a speech bubble
   * above each player's head (locally + on every other client).
   */
  private setupChat() {
    // T or "/" opens chat
    this.input.keyboard!.on("keydown-T", (e: KeyboardEvent) => {
      if (this.isInteracting) return;
      e.preventDefault?.();
      this.openChatInput();
    });
    this.input.keyboard!.on("keydown-SLASH", (e: KeyboardEvent) => {
      e.preventDefault?.();
      this.openChatInput();
    });
  }

  private openChatInput() {
    let input = this.chatInput;
    if (!input) {
      input = document.createElement("input");
      input.type = "text";
      input.maxLength = 140;
      input.placeholder = "Press Enter to send · Esc to cancel";
      input.style.cssText = `
        position: fixed; left: 50%; bottom: 28px;
        transform: translateX(-50%);
        width: min(640px, 80vw);
        background: rgba(15, 23, 42, 0.85);
        backdrop-filter: blur(16px) saturate(160%);
        border: 1px solid rgba(34, 211, 238, 0.4);
        border-radius: 12px;
        padding: 14px 18px;
        font-family: Inter, system-ui, sans-serif;
        font-size: 14px;
        color: #e2e8f0;
        outline: none;
        z-index: 30;
        box-shadow: 0 12px 36px rgba(0, 0, 0, 0.45);
      `;
      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          const text = input!.value.trim();
          if (text) this.sendChat(text);
          this.closeChatInput();
        } else if (e.key === "Escape") {
          this.closeChatInput();
        }
        e.stopPropagation();
      });
      input.addEventListener("keyup", (e) => e.stopPropagation());
      document.body.appendChild(input);
      this.chatInput = input;
    }
    input.value = "";
    input.style.display = "block";
    setTimeout(() => input!.focus(), 0);
  }

  private closeChatInput() {
    if (!this.chatInput) return;
    this.chatInput.value = "";
    this.chatInput.style.display = "none";
    this.chatInput.blur();
    (document.querySelector("canvas") as HTMLCanvasElement | null)?.focus();
  }

  private sendChat(text: string) {
    // Show locally immediately
    this.spawnChatBubble(this.mySessionId, text);
    // Broadcast
    this.room?.send("chat", { text });
  }

  /**
   * Render a speech bubble above the named player's sprite for 5 seconds.
   * Bubble is anchored to the sprite via per-tick repositioning in update().
   */
  private spawnChatBubble(sessionId: string, text: string) {
    // Kill any existing bubble for this player so they don't stack.
    const existing = this.chatBubbles.get(sessionId);
    if (existing) {
      existing.container.destroy();
      this.chatBubbles.delete(sessionId);
    }

    const padX = 10, padY = 6;
    const t = this.add.text(0, 0, text, {
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: "12px",
      fontStyle: "500",
      color: "#e2e8f0",
      wordWrap: { width: 200, useAdvancedWrap: true },
    }).setOrigin(0.5, 1);
    const bg = this.add.graphics();
    const w = t.width + padX * 2;
    const h = t.height + padY * 2;
    bg.fillStyle(0x0f172a, 0.92);
    bg.fillRoundedRect(-w / 2, -h, w, h, 8);
    bg.lineStyle(1, 0x22d3ee, 0.4);
    bg.strokeRoundedRect(-w / 2, -h, w, h, 8);
    // tail (small triangle) at the bottom
    bg.fillStyle(0x0f172a, 0.92);
    bg.fillTriangle(-4, 0, 4, 0, 0, 5);

    const container = this.add.container(0, 0, [bg, t]).setDepth(210);
    this.chatBubbles.set(sessionId, { container, expiresAt: this.time.now + 5000 });
  }

  private async connect() {
    try {
      this.client = new Client(COLYSEUS_URL);
      this.room = await this.client.joinOrCreate("one-heart", { token: this.token });
      this.mySessionId = this.room.sessionId;
      console.log(`[one-heart] joined as ${this.member.name} (${this.mySessionId})`);

      // Disconnection handling — show banner, attempt 3 reloads with backoff
      this.room.onLeave((code) => {
        const banner = document.getElementById("disconnect-banner");
        if (banner) banner.style.display = "block";
        console.warn(`[one-heart] room left, code=${code}`);
        // 4001 = newer-session-replaces-older; don't reload, just show banner
        if (code === 4001) {
          if (banner) banner.textContent = "⚠ another session opened — this tab is now read-only";
          return;
        }
        setTimeout(() => location.reload(), 2500);
      });

      // Initialise voice (mic permission prompt happens here)
      this.voice = new VoiceManager(this.room, this.mySessionId, (sid) => this.computeVolumeFor(sid));
      const result = await this.voice.start();
      this.muteBtn.setText(result.ok ? "🎤  live · click to mute" : "🔇  listen-only");

      // Claude presence updates → door lights
      this.room.onMessage("presence-snapshot", (msg: { entries: Array<{ memberId: string; status: string }> }) => {
        for (const e of msg.entries || []) this.applyDoorLight(e.memberId, e.status);
      });
      this.room.onMessage("presence-update", (entry: { memberId: string; status: string }) => {
        this.applyDoorLight(entry.memberId, entry.status);
      });

      // Weather updates → particle effect (rain/snow/clear)
      this.room.onMessage("weather-update", (msg: { type: string }) => {
        this.applyWeather(msg.type || "clear");
      });

      // Floor-wide announcements (from MCP broadcast_announcement tool)
      this.room.onMessage("announcement", (msg: { author?: string; message: string }) => {
        this.showAnnouncement(`${msg.author || "Claude"}: ${msg.message}`);
      });

      // Remote players hopping
      this.room.onMessage("jump", (msg: { from: string }) => {
        const v = this.remotePlayers.get(msg.from);
        if (v) this.triggerRemoteJump(v);
      });

      // Chat — render bubble above sender
      this.room.onMessage("chat", (msg: { from: string; text: string }) => {
        if (msg.from === this.mySessionId) return;        // already shown locally
        this.spawnChatBubble(msg.from, msg.text);
      });

      // Sims-style economy — coin HUD + shop modal (B key)
      this.shop = new Shop(this.token);
      this.shop.start().catch((e) => console.warn("[shop] start failed:", e));
      this.shop.onChange(() => {
        // After every wallet/inventory/equipped change, tell server to re-apply
        // our loadout so accentHex (halo colour) updates for all clients.
        this.room.send("equip-update");
        // Also refresh our own appearance — equipping a LimeZu item updates
        // the user's appearance JSON server-side; we need to re-read so the
        // local avatar visibly changes layers without waiting for room state.
        const apiBase = (location.port === "5173")
          ? `${location.protocol}//${location.hostname}:2567`
          : "";
        fetch(`${apiBase}/api/me/profile`, {
          headers: { Authorization: `Bearer ${this.token}` },
        }).then((r) => r.ok ? r.json() : null).then((data) => {
          if (data?.appearance && this.localAvatar) {
            this.localAvatar.setAppearance(data.appearance);
            this.appearance = { ...this.appearance, ...data.appearance };
            sessionStorage.setItem("one-heart-appearance", JSON.stringify(this.appearance));
          }
        }).catch(() => {});
      });

      this.bindRoomEvents();
    } catch (err: any) {
      console.error("[one-heart] failed to connect:", err);
      const code = err?.code;
      if (code === 401) {
        // Token expired — clear and force re-login
        sessionStorage.removeItem("one-heart-token");
        sessionStorage.removeItem("one-heart-member");
        location.reload();
        return;
      }
      this.add
        .text(this.cameras.main.centerX, this.cameras.main.centerY, "Connection failed.\nIs the backend running?", {
          fontFamily: "system-ui",
          fontSize: "16px",
          color: "#ff2e88",
          align: "center",
        })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(200);
    }
  }

  private bindRoomEvents() {
    const $ = getStateCallbacks(this.room);
    $(this.room.state).players.onAdd((player: any, sessionId: string) => {
      if (sessionId === this.mySessionId) {
        this.localBody.setPosition(player.x, player.y);
        this.localSprite.setPosition(player.x, player.y);
        this.localLabel.setPosition(player.x, player.y - 36);
        // Pick up server-assigned accent color (default per member)
        if (player.accentHex) {
          this.localAccent = player.accentHex;
          this.drawHalo(this.localHalo, this.localAccent);
        }
        // Initial loadout (in case the player joined with prior equipment)
        this.localItems.setHat(player.hatItemId || "");
        this.localItems.setHand(player.drinkItemId || "", player.carryItemId || "");
        this.localAvatar?.setEffect(fxItemToEffect(player.fxItemId || ""));
        let localFxId = player.fxItemId || "";

        // Listen for our own mood + accent + equipment changes
        $(player).onChange(() => {
          if (player.mood && player.mood !== this.localMood) {
            this.localMood = player.mood;
            this.localLabel.setColor(MOOD_COLORS[this.localMood] || "#ffffff");
          }
          if (player.accentHex && player.accentHex !== this.localAccent) {
            this.localAccent = player.accentHex;
            this.drawHalo(this.localHalo, this.localAccent);
          }
          this.localItems.setHat(player.hatItemId || "");
          this.localItems.setHand(player.drinkItemId || "", player.carryItemId || "");
          if (player.fxItemId !== localFxId) {
            localFxId = player.fxItemId || "";
            this.localAvatar?.setEffect(fxItemToEffect(localFxId));
          }
        });
        return;
      }
      const charKey = (MEMBER_TO_CHAR[player.memberId] ?? "adam") as CharKey;
      const sprite = makeSprite(this, player.x, player.y, charKey);
      sprite.setDepth(50);
      sprite.setVisible(false);              // procedural avatar replaces it
      const remoteAvatar = new CharacterAvatar(this, player.x, player.y, {
        bodyIdx:          player.bodyIdx          ?? DEFAULT_APPEARANCE.bodyIdx,
        eyesIdx:          player.eyesIdx          ?? DEFAULT_APPEARANCE.eyesIdx,
        hairFamily:       player.hairFamily       ?? DEFAULT_APPEARANCE.hairFamily,
        hairVariant:      player.hairVariant      ?? DEFAULT_APPEARANCE.hairVariant,
        outfitFamily:     player.outfitFamily     ?? DEFAULT_APPEARANCE.outfitFamily,
        outfitVariant:    player.outfitVariant    ?? DEFAULT_APPEARANCE.outfitVariant,
        headgearFamily:   player.headgearFamily   ?? DEFAULT_APPEARANCE.headgearFamily,
        headgearVariant:  player.headgearVariant  ?? DEFAULT_APPEARANCE.headgearVariant,
        accessoryFamily:  player.accessoryFamily  ?? DEFAULT_APPEARANCE.accessoryFamily,
        accessoryVariant: player.accessoryVariant ?? DEFAULT_APPEARANCE.accessoryVariant,
      });
      remoteAvatar.getContainer().setDepth(51);
      this.remoteAvatars.set(sessionId, remoteAvatar);
      const halo = this.add.graphics();
      halo.setDepth(49);
      this.drawHalo(halo, player.accentHex || "#00f0ff");
      halo.setPosition(player.x, player.y);
      const label = this.add
        .text(player.x, player.y - 36, player.name, {
          fontFamily: "system-ui",
          fontSize: "11px",
          color: MOOD_COLORS[player.mood] || "#ffffff",
          backgroundColor: "#000000aa",
          padding: { x: 4, y: 2 },
        })
        .setOrigin(0.5, 1)
        .setDepth(100)
        .setVisible(false);

      const items = new ItemRenderer(this);
      items.setHat(player.hatItemId || "");
      items.setHand(player.drinkItemId || "", player.carryItemId || "");
      remoteAvatar.setEffect(fxItemToEffect(player.fxItemId || ""));

      const visual: RemotePlayerVisual = {
        sprite, label, halo, items, charKey,
        targetX: player.x,
        targetY: player.y,
        lastX: player.x,
        lastY: player.y,
        bodyX: player.x,
        bodyY: player.y,
        jumpOffset: 0,
        isJumping: false,
        area: player.area || "lounge",
        direction: player.direction || "down",
        accentHex: player.accentHex || "#00f0ff",
        mood: player.mood || "normal",
        hatItemId: player.hatItemId || "",
        drinkItemId: player.drinkItemId || "",
        carryItemId: player.carryItemId || "",
        fxItemId: player.fxItemId || "",
      };
      this.remotePlayers.set(sessionId, visual);

      // Per-item onChange — Colyseus 0.16 collection.onChange is NOT recursive.
      // We must attach the field-level listener to the player instance itself,
      // otherwise x/y updates from the server never reach the visual.
      $(player).onChange(() => {
        visual.targetX = player.x;
        visual.targetY = player.y;
        if (player.area) visual.area = player.area;
        if (player.direction) visual.direction = player.direction;
        if (player.accentHex && player.accentHex !== visual.accentHex) {
          visual.accentHex = player.accentHex;
          this.drawHalo(visual.halo, visual.accentHex);
        }
        if (player.mood && player.mood !== visual.mood) {
          visual.mood = player.mood;
          visual.label.setColor(MOOD_COLORS[visual.mood] || "#ffffff");
        }
        if (player.hatItemId !== visual.hatItemId) {
          visual.hatItemId = player.hatItemId;
          visual.items.setHat(visual.hatItemId);
        }
        if (player.drinkItemId !== visual.drinkItemId || player.carryItemId !== visual.carryItemId) {
          visual.drinkItemId = player.drinkItemId;
          visual.carryItemId = player.carryItemId;
          visual.items.setHand(visual.drinkItemId, visual.carryItemId);
        }
        if (player.fxItemId !== visual.fxItemId) {
          visual.fxItemId = player.fxItemId || "";
          this.remoteAvatars.get(sessionId)?.setEffect(fxItemToEffect(visual.fxItemId));
        }
        // Live appearance updates (when remote player saves a new look)
        const av = this.remoteAvatars.get(sessionId);
        if (av) {
          av.setAppearance({
            bodyIdx:          player.bodyIdx,
            eyesIdx:          player.eyesIdx,
            hairFamily:       player.hairFamily,
            hairVariant:      player.hairVariant,
            outfitFamily:     player.outfitFamily,
            outfitVariant:    player.outfitVariant,
            headgearFamily:   player.headgearFamily,
            headgearVariant:  player.headgearVariant,
            accessoryFamily:  player.accessoryFamily,
            accessoryVariant: player.accessoryVariant,
          });
        }
      });

      // Open a voice peer with the new player
      this.voice?.onRemotePlayerAdded(sessionId);
    });

    $(this.room.state).players.onRemove((_player: any, sessionId: string) => {
      const v = this.remotePlayers.get(sessionId);
      if (!v) return;
      v.sprite.destroy();
      v.label.destroy();
      v.halo.destroy();
      v.items.destroy();
      this.nameplates.remove(sessionId);
      this.remotePlayers.delete(sessionId);
      const av = this.remoteAvatars.get(sessionId);
      if (av) { av.destroy(); this.remoteAvatars.delete(sessionId); }
      this.voice?.onRemotePlayerRemoved(sessionId);
    });
  }

  /** Draw a soft glowing oval used as a tier-coloured halo under each character. */
  private drawHalo(g: Phaser.GameObjects.Graphics, accentHex: string) {
    g.clear();
    const c = hexToInt(accentHex);
    // Halo is the ground-glow under the avatar. After moving the avatar to
    // body.y + 42 (so feet align with physics body bottom at body.y + 10),
    // the halo offset must match: ellipse centered at +10 sits exactly under
    // the avatar's feet. Previous offset (+18) left an 8-pixel "floating" gap.
    g.fillStyle(c, 0.35);
    g.fillEllipse(0, 10, 28, 10);
    g.fillStyle(c, 0.6);
    g.fillEllipse(0, 10, 18, 6);
    g.setBlendMode(Phaser.BlendModes.ADD);
  }

  /**
   * Tint the world based on Asia/Kuala_Lumpur time of day.
   *  - 06:00–18:00 → no overlay (clear daylight)
   *  - 18:00–20:00 → warm sunset tint (orange ×0.15 alpha, ramping)
   *  - 20:00–05:00 → night blue (×0.45 alpha)
   *  - 05:00–06:00 → dawn (orange ×0.2)
   * Updates every minute.
   */
  private updateDayNight() {
    const myt = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kuala_Lumpur" }));
    const h = myt.getHours();
    const m = myt.getMinutes();
    const decimal = h + m / 60;

    let color = 0x0a0828;
    let alpha = 0;
    if (decimal >= 6 && decimal < 18) { alpha = 0; }                            // day
    else if (decimal >= 18 && decimal < 20) {                                   // sunset
      color = 0xff6e3a;
      alpha = 0.05 + ((decimal - 18) / 2) * 0.15;
    } else if (decimal >= 20 || decimal < 5) {                                  // night
      color = 0x0a0828;
      alpha = 0.45;
    } else {                                                                    // dawn 5-6
      color = 0xff8c40;
      alpha = 0.2 - ((decimal - 5) / 1) * 0.15;
    }
    this.dayNightOverlay.setFillStyle(color, alpha);
  }

  /** Toggle weather particles based on broadcast weather state. */
  private applyWeather(type: string) {
    if (this.weatherEmitter) {
      this.weatherEmitter.destroy();
      this.weatherEmitter = null;
    }
    if (type === "clear") return;

    // Lightweight particle generator — we draw on a 2x2 graphics texture once.
    const txKey = type === "snow" ? "weather-snow" : "weather-rain";
    if (!this.textures.exists(txKey)) {
      const g = this.add.graphics().setVisible(false);
      if (type === "snow") {
        g.fillStyle(0xffffff, 1);
        g.fillCircle(2, 2, 2);
      } else {
        g.fillStyle(0xa5f3fc, 1);
        g.fillRect(0, 0, 2, 8);
      }
      g.generateTexture(txKey, type === "snow" ? 4 : 2, type === "snow" ? 4 : 8);
      g.destroy();
    }

    this.weatherEmitter = this.add.particles(0, 0, txKey, {
      x: { min: 0, max: MAP_W_PX },
      y: -10,
      lifespan: type === "snow" ? 6000 : 1800,
      speedY: type === "snow" ? { min: 30, max: 60 } : { min: 320, max: 420 },
      speedX: type === "snow" ? { min: -20, max: 20 } : { min: -10, max: 10 },
      alpha: { start: 0.7, end: 0.2 },
      scale: type === "snow" ? { min: 0.6, max: 1.4 } : { min: 0.8, max: 1.1 },
      frequency: type === "snow" ? 60 : 18,
      quantity: 1,
    });
    this.weatherEmitter.setDepth(140);
    this.weatherEmitter.setBlendMode(Phaser.BlendModes.ADD);
  }

  private showAnnouncement(text: string) {
    const banner = this.add
      .text(this.cameras.main.width / 2, 70, text, {
        fontFamily: "system-ui",
        fontSize: "13px",
        color: "#fde047",
        backgroundColor: "#1a1430ee",
        padding: { x: 14, y: 8 },
        wordWrap: { width: 600 },
        align: "center",
      })
      .setScrollFactor(0)
      .setOrigin(0.5, 0)
      .setDepth(220);
    this.tweens.add({
      targets: banner,
      alpha: { from: 1, to: 0 },
      duration: 800,
      delay: 7000,
      onComplete: () => banner.destroy(),
    });
  }

  /**
   * Spatial volume rule:
   *  - Different areas (one in room A, the other elsewhere) → 0 (walls block sound)
   *  - Same private room → 1.0 (rooms are small enough to be fully heard)
   *  - Both in lounge → linear falloff between VOICE_FULL_RANGE_PX and VOICE_RANGE_PX
   */
  private applyDoorLight(memberId: string, status: string) {
    const lightState =
      status === "online" ? "in-claude" :
      status === "idle"   ? "online"    :
      "off";
    this.mapRenderer.setDoorLight(memberId, lightState as any);
  }

  private computeVolumeFor(remoteSessionId: string): number {
    const remote = this.remotePlayers.get(remoteSessionId);
    if (!remote) return 0;
    if (remote.area !== this.currentArea) return 0;
    if (remote.area !== "lounge") return 1; // inside a private room = full

    const dx = remote.bodyX - this.localBody.x;
    const dy = remote.bodyY - this.localBody.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist <= VOICE_FULL_RANGE_PX) return 1;
    if (dist >= VOICE_RANGE_PX) return 0;
    return 1 - (dist - VOICE_FULL_RANGE_PX) / (VOICE_RANGE_PX - VOICE_FULL_RANGE_PX);
  }

  private tryLocalJump() {
    if (this.isLocalJumping) return;
    this.isLocalJumping = true;
    this.lastInputAt = this.time.now;
    if (this.isIdle) {
      this.isIdle = false;
      this.localLabel.setColor("#ffffff");
    }
    this.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 400,
      onUpdate: (tw) => { this.localJumpOffset = -16 * Math.sin(tw.getValue() * Math.PI); },
      onComplete: () => { this.localJumpOffset = 0; this.isLocalJumping = false; },
    });
    this.room?.send("jump");
  }

  /**
   * Reposition every active chat bubble above its owner's sprite, fade out
   * those past their expiry, and destroy them when fully transparent.
   */
  private tickChatBubbles(now: number) {
    for (const [sid, bubble] of this.chatBubbles) {
      // Find anchor — local player or one of the remote players
      let ax: number | null = null, ay: number | null = null;
      if (sid === this.mySessionId) {
        ax = this.localBody.x;
        ay = this.localBody.y - 56;
      } else {
        const v = this.remotePlayers.get(sid);
        if (v) { ax = v.bodyX; ay = v.bodyY - 56; }
      }
      if (ax === null || ay === null) {
        bubble.container.destroy();
        this.chatBubbles.delete(sid);
        continue;
      }
      bubble.container.setPosition(ax, ay);

      // Fade-out in the last 600ms before expiry
      const remaining = bubble.expiresAt - now;
      if (remaining <= 0) {
        bubble.container.destroy();
        this.chatBubbles.delete(sid);
      } else if (remaining < 600) {
        bubble.container.setAlpha(remaining / 600);
      }
    }
  }

  private triggerRemoteJump(visual: RemotePlayerVisual) {
    if (visual.isJumping) return;
    visual.isJumping = true;
    this.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 400,
      onUpdate: (tw) => { visual.jumpOffset = -16 * Math.sin(tw.getValue() * Math.PI); },
      onComplete: () => { visual.jumpOffset = 0; visual.isJumping = false; },
    });
  }

  update(_time: number, delta: number) {
    // Tick interactions every frame so the [E] hint follows the player.
    this.interactions?.tick();

    // Update chat-bubble positions + expire stale ones
    this.tickChatBubbles(_time);

    let dx = 0;
    let dy = 0;
    // While sitting/in-modal, ignore movement input. Interactions still tick.
    if (!this.isInteracting) {
      if (this.cursors.left.isDown || this.wasd.A.isDown) dx -= 1;
      if (this.cursors.right.isDown || this.wasd.D.isDown) dx += 1;
      if (this.cursors.up.isDown || this.wasd.W.isDown) dy -= 1;
      if (this.cursors.down.isDown || this.wasd.S.isDown) dy += 1;
      // Mobile joystick input — overrides keyboard if joystick is engaged
      // (>5% from centre to debounce drift). Uses the analog magnitude so
      // small joystick deflections produce correspondingly slower movement.
      const jdist = Math.hypot(this.mobileDir.x, this.mobileDir.y);
      if (jdist > 0.05) {
        dx = this.mobileDir.x;
        dy = this.mobileDir.y;
      }
    }

    const moving = Math.abs(dx) > 0.05 || Math.abs(dy) > 0.05;
    if (moving) {
      this.lastInputAt = _time;
      if (this.isIdle) {
        this.isIdle = false;
        this.localLabel.setColor("#ffffff");
      }
    } else if (!this.isIdle && _time - this.lastInputAt > 5 * 60 * 1000) {
      this.isIdle = true;
      this.localLabel.setColor("#9ca3af");
    }
    if (moving) {
      const len = Math.sqrt(dx * dx + dy * dy);
      dx /= len;
      dy /= len;
      this.localBody.body.setVelocity(dx * SPEED_PX_PER_SEC, dy * SPEED_PX_PER_SEC);
      if (Math.abs(dx) > Math.abs(dy)) this.direction = dx > 0 ? "right" : "left";
      else this.direction = dy > 0 ? "down" : "up";
    } else {
      this.localBody.body.setVelocity(0, 0);
    }

    this.localSprite.setPosition(this.localBody.x, this.localBody.y + this.localJumpOffset + this.sitOffset);
    this.localLabel.setPosition(this.localBody.x, this.localBody.y - 36);
    this.localHalo.setPosition(this.localBody.x, this.localBody.y);
    this.localItems?.syncTo(this.localBody.x, this.localBody.y + this.localJumpOffset + this.sitOffset, this.direction);
    playMove(this.localSprite, this.localCharKey, this.direction, moving && !this.isInteracting);

    // Procedural avatar — feet aligned with physics body bottom.
    // Container origin (0.5, 0); body sprite at local (0, -16) origin (0.5, 1).
    // With container scale 2, sprite bottom is at world y = container_y - 32.
    // Physics body bottom is at body.y + 10 (14×14 body inside 16×24 rect with
    // setOffset(1, 8)). To put feet at body bottom: container_y = body.y + 42.
    if (this.localAvatar) {
      this.localAvatar.setPosition(
        this.localBody.x,
        this.localBody.y + 42 + this.localJumpOffset + this.sitOffset,
      );
      this.localAvatar.setDirection(this.direction);
      this.localAvatar.setMoving(moving && !this.isInteracting);
      this.localAvatar.update(delta);
    }

    // Glassmorphism HTML nameplate above local player.
    // Use cam.worldView (the actual rendered world rect) instead of
    // scrollX, because Phaser's scrollX has a half-viewport offset when
    // zoomed. worldView.x is the world coord at screen X=0.
    {
      const cam = this.cameras.main;
      const screenX = (this.localBody.x - cam.worldView.x) * cam.zoom;
      const screenY = (this.localBody.y - cam.worldView.y - 110 + this.localJumpOffset) * cam.zoom;
      this.nameplates.syncTo("local", screenX, screenY, {
        name: this.member.name,
        mood: this.localMood,
        haloHex: this.localAccent,
        presence: "online",
      });
      // WallScreen TV follows camera too
      this.wallScreen?.syncToCamera(cam.scrollX, cam.scrollY);
    }

    // Track current area
    const newArea = locateArea(this.localBody.x, this.localBody.y);
    if (newArea !== this.currentArea) {
      this.currentArea = newArea;
      this.areaHud.setText(`in · ${newArea}`);
    }

    // Broadcast position
    if (this.room && _time - this.lastBroadcast > BROADCAST_INTERVAL_MS) {
      this.room.send("move", {
        x: this.localBody.x,
        y: this.localBody.y,
        direction: this.direction,
        moving,
        area: this.currentArea,
      });
      this.lastBroadcast = _time;
    }

    // Smooth remote interpolation + animation. bodyX/bodyY hold the logical
    // position; sprite Y adds jumpOffset so the hop doesn't drift interpolation.
    for (const [sid, v] of this.remotePlayers.entries()) {
      const px = v.bodyX;
      const py = v.bodyY;
      v.bodyX += (v.targetX - v.bodyX) * INTERP_RATE;
      v.bodyY += (v.targetY - v.bodyY) * INTERP_RATE;
      v.sprite.setPosition(v.bodyX, v.bodyY + v.jumpOffset);
      v.label.setPosition(v.bodyX, v.bodyY - 36);
      v.halo.setPosition(v.bodyX, v.bodyY);
      v.items.syncTo(v.bodyX, v.bodyY + v.jumpOffset, v.direction);
      const moved = Math.hypot(v.bodyX - px, v.bodyY - py) > 0.4;
      playMove(v.sprite, v.charKey, v.direction, moved);

      // Procedural remote avatar — same +42 as local for feet alignment.
      const av = this.remoteAvatars.get(sid);
      if (av) {
        av.setPosition(v.bodyX, v.bodyY + 42 + v.jumpOffset);
        av.setDirection(v.direction);
        av.setMoving(moved);
        av.update(delta);
      }
    }

    // Sync glassmorphism HTML nameplates for all remote players (worldView).
    {
      const cam = this.cameras.main;
      for (const [sid, v] of this.remotePlayers.entries()) {
        const screenX = (v.bodyX - cam.worldView.x) * cam.zoom;
        const screenY = (v.bodyY - cam.worldView.y - 110 + v.jumpOffset) * cam.zoom;
        this.nameplates.syncTo(sid, screenX, screenY, {
          name: v.label.text || "?",
          mood: v.mood,
          haloHex: v.accentHex,
          presence: "online",
        });
      }
    }

    // Minimap: local dot every frame, remote dots from current snapshot.
    this.minimap.updatePlayer(this.localBody.x, this.localBody.y);
    this.minimap.updateRemotes(
      Array.from(this.remotePlayers.entries()).map(([id, v]) => ({
        id,
        x: v.bodyX,
        y: v.bodyY,
        color: v.accentHex,
      })),
    );

    // Update voice volumes based on current spatial state
    this.voice?.applyVolumes();
  }
}
